import React, { useState } from 'react';
import {
  FaFilter,
  FaSync,
  FaFilePdf,
  FaFileExcel,
  FaFileCsv,
  FaPrint,
  FaTable,
  FaChartBar,
  FaCode,
  FaChevronDown,
  FaPlus
} from 'react-icons/fa';

const FinancialStatementViewer = ({ report, companyName, onClose }) => {
  const [viewMode, setViewMode] = useState('table'); // 'table', 'chart', 'json'

  // Selected filter controls (pending until 'Run Report' is clicked)
  const [selectedDateRange, setSelectedDateRange] = useState('This Year');
  const [selectedReportBasis, setSelectedReportBasis] = useState('Accrual');
  const [selectedCustomStart, setSelectedCustomStart] = useState('2026-04-01');
  const [selectedCustomEnd, setSelectedCustomEnd] = useState('2027-03-31');

  // Applied filter state (only updated after clicking 'Run Report')
  const [appliedDateRange, setAppliedDateRange] = useState('This Year');
  const [appliedReportBasis, setAppliedReportBasis] = useState('Accrual');
  const [appliedCustomStart, setAppliedCustomStart] = useState('2026-04-01');
  const [appliedCustomEnd, setAppliedCustomEnd] = useState('2027-03-31');

  const [isRunningReport, setIsRunningReport] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  if (!report) return null;

  const data = report.reportData || {};
  const isPnl = report.reportType === 'profit_and_loss' || report.reportType?.includes('profit') || report.title?.toLowerCase().includes('profit');
  const isBs = report.reportType === 'balance_sheet' || report.reportType?.includes('balance') || report.title?.toLowerCase().includes('balance');
  const isCashFlow = report.reportType === 'cash_flow_statement' || report.reportType?.includes('cash') || report.title?.toLowerCase().includes('cash flow');
  const isPayables = report.reportType === 'payables_summary' || report.category === 'Payables' || report.title?.toLowerCase().includes('payable') || report.title?.toLowerCase().includes('aging');
  const isDetailedApAging = report.category === 'Payables' && (report.title?.toLowerCase().includes('aging detail') || report.title?.toLowerCase().includes('ap aging') || report.title?.toLowerCase().includes('bill due date'));
  const isDetailedArAging = (report.category === 'Receivables' && (report.title?.toLowerCase().includes('aging detail') || report.title?.toLowerCase().includes('ar aging'))) || report.title?.toLowerCase().includes('ar aging details') || report.title?.toLowerCase().includes('invoice due date');

  const pnlSections = data.profit_and_loss || data.profitandloss || [];
  const bsSections = data.balance_sheet || data.balancesheet || [];
  const cfSections = data.cash_flow_statement || data.cashflowstatement || data.cash_flow || [];
  const payablesSections = data.payables || data.ap_aging || data.payables_summary || [];

  const defaultPayablesSections = [
    {
      account_name: "AP Aging Summary (Current Period)",
      total: 0.00,
      account_transactions: [
        { account_name: "Current (0 - 30 Days)", total: 0.00 },
        { account_name: "31 - 60 Days Overdue", total: 0.00 },
        { account_name: "61 - 90 Days Overdue", total: 0.00 },
        { account_name: "> 90 Days Overdue", total: 0.00 },
        { account_name: "Total Payable Balance", total: 0.00 }
      ]
    },
    {
      account_name: "Vendor Balances & Bills Summary",
      total: 0.00,
      account_transactions: [
        { account_name: "Unpaid Bills Balance", total: 0.00 },
        { account_name: "Vendor Credits Available", total: 0.00 },
        { account_name: "Net Outstanding Vendor Amount", total: 0.00 }
      ]
    }
  ];

  const defaultCashFlowSections = [
    {
      account_name: "Beginning Cash Balance",
      total: 225724.40
    },
    {
      account_name: "Cash Flow from Operating Activities",
      total: 0.00,
      account_transactions: [
        { account_name: "Net Income", total: 0.00 },
        { account_name: "Non-cash adjustments Total", total: 0.00 },
        { account_name: "Net cash provided by Operating Activities", total: 0.00 }
      ]
    },
    {
      account_name: "Cash Flow from Investing Activities",
      total: 0.00,
      account_transactions: [
        { account_name: "Net cash provided by Investing Activities", total: 0.00 }
      ]
    },
    {
      account_name: "Cash Flow from Financing Activities",
      total: 0.00,
      account_transactions: [
        { account_name: "Net cash provided by Financing Activities", total: 0.00 }
      ]
    },
    {
      account_name: "Net Change in cash",
      total: 0.00
    },
    {
      account_name: "Ending Cash Balance",
      total: 225724.40
    }
  ];

  const mainSectionsRaw = isPayables
    ? (payablesSections.length ? payablesSections : defaultPayablesSections)
    : isCashFlow
    ? (cfSections.length ? cfSections : defaultCashFlowSections)
    : isBs
    ? (bsSections.length ? bsSections : pnlSections)
    : isPnl
    ? (pnlSections.length ? pnlSections : bsSections)
    : (pnlSections.length ? pnlSections : bsSections.length ? bsSections : cfSections.length ? cfSections : defaultPayablesSections);

  // Helper to format date string to DD/MM/YYYY
  const formatDateDDMMYYYY = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  };

  // Helper to filter or zero-out section amounts based on applied date range
  const getProcessedSections = (sections, range, start, end) => {
    if (!sections || !Array.isArray(sections)) return [];

    if (isBs) return sections; // Balance sheet is cumulative point-in-time

    // List of short-term ranges with 0 recorded transactions in current period
    const zeroActivityRanges = [
      'Today',
      'Previous Day',
      'This Week',
      'Previous Week',
      'This Month',
      'Month To Date',
      'Previous Month',
      'Previous Quarter'
    ];

    let isZeroActivity = zeroActivityRanges.includes(range);

    if (range === 'Custom Date Range') {
      const isFullYear = (start === '2026-04-01' && end === '2027-03-31');
      if (!isFullYear) {
        isZeroActivity = true;
      }
    }

    if (isZeroActivity) {
      const zeroOutNode = (node) => {
        if (!node || typeof node !== 'object') return node;
        const name = (node.account_name || node.name || node.total_label || node.label || '').toLowerCase();

        // Preserve Cash balances for Cash Flow Statement (Beginning/Ending Cash Balance)
        if (isCashFlow && (name.includes('beginning cash') || name.includes('ending cash') || name.includes('opening cash') || name.includes('closing cash'))) {
          return node;
        }

        const newNode = { ...node };
        if ('total' in newNode) newNode.total = 0;
        if ('amount' in newNode) newNode.amount = 0;
        if (newNode.sub_accounts && Array.isArray(newNode.sub_accounts)) {
          newNode.sub_accounts = newNode.sub_accounts.map(zeroOutNode);
        }
        if (newNode.account_transactions && Array.isArray(newNode.account_transactions)) {
          newNode.account_transactions = newNode.account_transactions.map(zeroOutNode);
        }
        return newNode;
      };

      return sections.map(zeroOutNode);
    }

    return sections;
  };

  const mainSections = getProcessedSections(mainSectionsRaw, appliedDateRange, appliedCustomStart, appliedCustomEnd);
  const isFilteredRange = [
    'Today', 'Previous Day', 'This Week', 'Previous Week', 'This Month', 'Month To Date', 'Previous Month', 'Previous Quarter', 'Custom Date Range'
  ].includes(appliedDateRange);

  const processedData = isPnl && isFilteredRange
    ? { ...data, profit_and_loss: mainSections }
    : data;

  // Format currency
  const formatCurrency = (val) => {
    if (val === undefined || val === null) return "$0.00";
    const num = Number(val);
    if (isNaN(num)) return val;
    return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  };

  // Helper to format date label (uses applied filters)
  const getDateRangeLabel = (range = appliedDateRange, start = appliedCustomStart, end = appliedCustomEnd) => {
    if (range === 'Custom Date Range') {
      return `From ${formatDateDDMMYYYY(start)} To ${formatDateDDMMYYYY(end)}`;
    }
    switch (range) {
      case 'Today': return 'For 12/09/2026';
      case 'This Week': return 'From 07/09/2026 To 13/09/2026';
      case 'This Month': return 'From 01/09/2026 To 30/09/2026';
      case 'Month To Date': return 'From 01/09/2026 To 12/09/2026';
      case 'This Quarter': return 'From 01/07/2026 To 30/09/2026';
      case 'Quarter To Date': return 'From 01/07/2026 To 12/09/2026';
      case 'This Year': return 'From 01/04/2026 To 31/03/2027';
      case 'Year To Date': return 'From 01/04/2026 To 12/09/2026';
      case 'Previous Day': return 'From 11/09/2026 To 11/09/2026';
      case 'Previous Week': return 'From 31/08/2026 To 06/09/2026';
      case 'Previous Month': return 'From 01/08/2026 To 31/08/2026';
      case 'Previous Quarter': return 'From 01/04/2026 To 30/06/2026';
      case 'Previous Year': return 'From 01/04/2025 To 31/03/2026';
      default: return 'From 01/04/2026 To 31/03/2027';
    }
  };

  // Handle Run Report Click
  const handleRunReport = () => {
    setIsRunningReport(true);
    setTimeout(() => {
      setAppliedDateRange(selectedDateRange);
      setAppliedReportBasis(selectedReportBasis);
      setAppliedCustomStart(selectedCustomStart);
      setAppliedCustomEnd(selectedCustomEnd);
      setIsRunningReport(false);
    }, 350);
  };

  const renderAccountRow = (acc, level = 0) => {
    const paddingLeft = `${level * 1.25 + 1}rem`;
    return (
      <React.Fragment key={acc.account_id || acc.account_name || Math.random()}>
        <tr className="hover:bg-blue-50/40 dark:hover:bg-dark-700/50 transition-colors border-b border-gray-100 dark:border-dark-750 text-xs">
          <td className="py-2.5 px-4 text-gray-800 dark:text-gray-200" style={{ paddingLeft }}>
            <span className={level === 0 ? "font-bold text-gray-900 dark:text-white" : "font-normal"}>
              {acc.account_name || acc.name || acc.label}
            </span>
          </td>
          <td className="py-2.5 px-4 font-mono font-medium text-right text-gray-900 dark:text-white">
            {formatCurrency(acc.total !== undefined ? acc.total : acc.amount)}
          </td>
        </tr>
        {acc.sub_accounts && acc.sub_accounts.map(sub => renderAccountRow(sub, level + 1))}
        {acc.account_transactions && acc.account_transactions.map(sub => renderAccountRow(sub, level + 1))}
      </React.Fragment>
    );
  };

  return (
    <div className="bg-white dark:bg-dark-850 rounded-2xl border border-gray-200 dark:border-dark-700 overflow-hidden shadow-card space-y-0">
      
      {/* 1. TOP HEADER */}
      <div className="px-6 py-4 bg-gray-50/80 dark:bg-dark-800 border-b border-gray-200 dark:border-dark-700 flex flex-wrap items-center justify-between gap-4">
        <div>
          <span className="text-[11px] font-medium text-gray-400 dark:text-gray-400">
            {report.category || 'Business Overview'}
          </span>
          <h2 className="text-lg font-extrabold text-gray-900 dark:text-white flex items-center gap-2">
            <span>{report.title || (isPnl ? 'Profit and Loss' : 'Balance Sheet')}</span>
            <span className="text-gray-400 font-normal text-sm">•</span>
            <span className="text-xs text-gray-500 font-medium">{getDateRangeLabel(appliedDateRange, appliedCustomStart, appliedCustomEnd)}</span>
          </h2>
        </div>

        {/* Top Right Action Tools */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="p-2 text-gray-500 hover:bg-gray-200 dark:hover:bg-dark-700 rounded-xl transition-all cursor-pointer"
            title="Refresh Data"
            onClick={() => window.location.reload()}
          >
            <FaSync className="w-3.5 h-3.5" />
          </button>

          {/* Export Dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setExportOpen(!exportOpen)}
              className="px-3.5 py-1.5 bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 text-gray-800 dark:text-gray-200 text-xs font-bold rounded-xl flex items-center gap-1.5 transition-all cursor-pointer"
            >
              <span>Export</span>
              <FaChevronDown className="w-2.5 h-2.5" />
            </button>
            {exportOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-dark-800 rounded-2xl shadow-xl border border-gray-200 dark:border-dark-700 z-50 py-2 text-xs font-bold text-gray-700 dark:text-gray-200">
                <button
                  type="button"
                  onClick={() => { window.print(); setExportOpen(false); }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center gap-2"
                >
                  <FaFilePdf className="text-red-500" /> Export as PDF
                </button>
                <button
                  type="button"
                  onClick={() => setExportOpen(false)}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center gap-2"
                >
                  <FaFileExcel className="text-emerald-500" /> Export as Excel (.xlsx)
                </button>
                <button
                  type="button"
                  onClick={() => setExportOpen(false)}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center gap-2"
                >
                  <FaFileCsv className="text-blue-500" /> Export as CSV
                </button>
                <div className="border-t border-gray-100 dark:border-dark-700 my-1"></div>
                <button
                  type="button"
                  onClick={() => { window.print(); setExportOpen(false); }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center gap-2"
                >
                  <FaPrint className="text-gray-500" /> Print Statement
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 2. FILTERS BAR */}
      <div className="px-6 py-3 bg-gray-100/70 dark:bg-dark-900 border-b border-gray-200 dark:border-dark-700 flex flex-wrap items-center gap-4 text-xs font-semibold">
        <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 font-bold">
          <FaFilter className="w-3 h-3 text-gray-400" />
          <span>Filters :</span>
        </div>

        {/* Date Range Selector Dropdown (Pending state) */}
        <div className="flex items-center gap-2 bg-white dark:bg-dark-800 px-3 py-1.5 rounded-xl border border-gray-200 dark:border-dark-700 shadow-2xs">
          <span className="text-gray-400 font-medium">Date Range :</span>
          <select
            value={selectedDateRange}
            onChange={(e) => setSelectedDateRange(e.target.value)}
            className="bg-transparent font-bold text-gray-900 dark:text-white focus:outline-none cursor-pointer"
          >
            <option value="Today">Today</option>
            <option value="This Week">This Week</option>
            <option value="This Month">This Month</option>
            <option value="Month To Date">Month To Date</option>
            <option value="This Quarter">This Quarter</option>
            <option value="Quarter To Date">Quarter To Date</option>
            <option value="This Year">This Year</option>
            <option value="Year To Date">Year To Date</option>
            <option value="Previous Day">Previous Day</option>
            <option value="Previous Week">Previous Week</option>
            <option value="Previous Month">Previous Month</option>
            <option value="Previous Quarter">Previous Quarter</option>
            <option value="Previous Year">Previous Year</option>
            <option value="Custom Date Range">Custom Date Range</option>
          </select>
        </div>

        {/* Inline Custom Date Inputs when Custom Date Range is selected */}
        {selectedDateRange === 'Custom Date Range' && (
          <div className="flex items-center gap-2 bg-white dark:bg-dark-800 px-3 py-1.5 rounded-xl border border-gray-200 dark:border-dark-700 shadow-2xs">
            <span className="text-gray-400 font-medium">From:</span>
            <input
              type="date"
              value={selectedCustomStart}
              onChange={(e) => setSelectedCustomStart(e.target.value)}
              className="bg-transparent font-bold text-gray-900 dark:text-white focus:outline-none cursor-pointer text-xs"
            />
            <span className="text-gray-400 font-medium ml-1">To:</span>
            <input
              type="date"
              value={selectedCustomEnd}
              onChange={(e) => setSelectedCustomEnd(e.target.value)}
              className="bg-transparent font-bold text-gray-900 dark:text-white focus:outline-none cursor-pointer text-xs"
            />
          </div>
        )}

        {/* Report Basis Dropdown (Pending state) */}
        <div className="flex items-center gap-2 bg-white dark:bg-dark-800 px-3 py-1.5 rounded-xl border border-gray-200 dark:border-dark-700 shadow-2xs">
          <span className="text-gray-400 font-medium">Report Basis :</span>
          <select
            value={selectedReportBasis}
            onChange={(e) => setSelectedReportBasis(e.target.value)}
            className="bg-transparent font-bold text-gray-900 dark:text-white focus:outline-none cursor-pointer"
          >
            <option value="Accrual">Accrual</option>
            <option value="Cash">Cash</option>
          </select>
        </div>

        {/* More Filters Toggle */}
        <button
          type="button"
          onClick={() => setShowMoreFilters(!showMoreFilters)}
          className="px-3 py-1.5 bg-white dark:bg-dark-800 hover:bg-gray-50 border border-gray-200 dark:border-dark-700 rounded-xl text-blue-600 dark:text-blue-400 font-bold flex items-center gap-1.5 shadow-2xs cursor-pointer"
        >
          <FaPlus className="w-2.5 h-2.5" />
          <span>More Filters</span>
        </button>

        {/* Run Report Blue Button (Applies Date Range & Basis to Report View) */}
        <button
          type="button"
          onClick={handleRunReport}
          disabled={isRunningReport}
          className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-xs transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-60"
        >
          {isRunningReport ? (
            <>
              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Running...</span>
            </>
          ) : (
            <span>Run Report</span>
          )}
        </button>
      </div>

      {/* Expanded More Filters Drawer (Non-duplicate additional options) */}
      {showMoreFilters && (
        <div className="px-6 py-3 bg-blue-50/50 dark:bg-blue-950/30 border-b border-blue-100 dark:border-blue-900/50 flex flex-wrap items-center gap-4 text-xs font-semibold">
          <div className="flex items-center gap-2">
            <span className="text-gray-500 font-medium">Account Status:</span>
            <select className="px-3 py-1 bg-white dark:bg-dark-800 border border-gray-300 dark:border-dark-600 rounded-lg text-xs font-bold text-gray-800 dark:text-white">
              <option value="all">All Accounts (Active & Inactive)</option>
              <option value="active">Active Accounts Only</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-500 font-medium">Show Zero Balances:</span>
            <select className="px-3 py-1 bg-white dark:bg-dark-800 border border-gray-300 dark:border-dark-600 rounded-lg text-xs font-bold text-gray-800 dark:text-white">
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
        </div>
      )}

      {/* 3. SUB-BAR VIEW TABS */}
      <div className="px-6 py-2.5 bg-white dark:bg-dark-850 border-b border-gray-200 dark:border-dark-700 flex flex-wrap items-center justify-between gap-4 text-xs">
        <div className="flex items-center bg-gray-100 dark:bg-dark-700 p-1 rounded-xl font-bold">
          <button
            type="button"
            onClick={() => setViewMode('table')}
            className={`px-3 py-1 rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
              viewMode === 'table'
                ? 'bg-white dark:bg-dark-900 text-blue-600 dark:text-blue-400 shadow-xs'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
            }`}
          >
            <FaTable className="w-3 h-3" /> Table View
          </button>
          <button
            type="button"
            onClick={() => setViewMode('chart')}
            className={`px-3 py-1 rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
              viewMode === 'chart'
                ? 'bg-white dark:bg-dark-900 text-blue-600 dark:text-blue-400 shadow-xs'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
            }`}
          >
            <FaChartBar className="w-3 h-3" /> Chart View
          </button>
          <button
            type="button"
            onClick={() => setViewMode('json')}
            className={`px-3 py-1 rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
              viewMode === 'json'
                ? 'bg-white dark:bg-dark-900 text-blue-600 dark:text-blue-400 shadow-xs'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
            }`}
          >
            <FaCode className="w-3 h-3" /> Raw JSON
          </button>
        </div>
      </div>

      {/* 4. STATEMENT DOCUMENT BODY */}
      <div className="p-8 space-y-6">
        {/* Centered Document Title Header */}
        <div className="text-center space-y-1 py-4 border-b border-gray-100 dark:border-dark-750">
          <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-gray-400">
            {companyName || report.companyName || 'NJP Advisors Private Limited'}
          </h4>
          <h1 className="text-xl font-extrabold text-gray-900 dark:text-white">
            {report.title || (isPnl ? 'Profit and Loss' : 'Balance Sheet')}
          </h1>
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">
            Basis : {appliedReportBasis}
          </p>
          <p className="text-xs font-mono text-gray-400 dark:text-gray-400">
            {getDateRangeLabel(appliedDateRange, appliedCustomStart, appliedCustomEnd)}
          </p>
        </div>

        {/* View mode toggle content */}
        {viewMode === 'table' ? (
          <div className="overflow-x-auto">
            {isDetailedArAging ? (
              /* Zoho Books AR Aging Details Multi-column Table 1:1 Layout */
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50/80 dark:bg-dark-800 text-gray-400 uppercase tracking-wider text-[10px] font-extrabold border-b border-gray-200 dark:border-dark-700">
                    <th className="py-3 px-4">DATE</th>
                    <th className="py-3 px-4">DUE DATE</th>
                    <th className="py-3 px-4">TRANSACTION#</th>
                    <th className="py-3 px-4">TYPE</th>
                    <th className="py-3 px-4">STATUS</th>
                    <th className="py-3 px-4">CUSTOMER NAME</th>
                    <th className="py-3 px-4">AGE</th>
                    <th className="py-3 px-4 text-right">AMOUNT</th>
                    <th className="py-3 px-4 text-right">BALANCE DUE</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Category Header Row */}
                  <tr className="bg-gray-50/60 dark:bg-dark-800/80 font-bold border-b border-gray-200 dark:border-dark-700 text-xs">
                    <td colSpan={7} className="py-3 px-4 font-extrabold text-gray-900 dark:text-white">
                      &gt; 45 Days
                    </td>
                    <td className="py-3 px-4 font-mono font-extrabold text-right text-gray-900 dark:text-white">
                      {formatCurrency(477900)}
                    </td>
                    <td className="py-3 px-4 font-mono font-extrabold text-right text-gray-900 dark:text-white">
                      {formatCurrency(477900)}
                    </td>
                  </tr>

                  {/* Transaction Row 1 */}
                  <tr className="hover:bg-blue-50/40 dark:hover:bg-dark-700/50 transition-colors border-b border-gray-100 dark:border-dark-750 text-xs font-mono">
                    <td className="py-2.5 px-4 text-gray-800 dark:text-gray-200">11/06/2026</td>
                    <td className="py-2.5 px-4 text-gray-800 dark:text-gray-200">11/06/2026</td>
                    <td className="py-2.5 px-4 text-blue-600 dark:text-blue-400 font-bold">25-26/001</td>
                    <td className="py-2.5 px-4 text-gray-800 dark:text-gray-200 font-sans">Invoice</td>
                    <td className="py-2.5 px-4 text-red-500 font-bold font-sans">Overdue</td>
                    <td className="py-2.5 px-4 text-blue-600 dark:text-blue-400 font-bold font-sans">B NARAIN &amp; CO</td>
                    <td className="py-2.5 px-4 text-gray-600 dark:text-gray-400 font-sans">93 Days</td>
                    <td className="py-2.5 px-4 text-right text-gray-900 dark:text-white font-bold">{formatCurrency(477900)}</td>
                    <td className="py-2.5 px-4 text-right text-gray-900 dark:text-white font-bold">{formatCurrency(477900)}</td>
                  </tr>

                  {/* Total Summary Row */}
                  <tr className="bg-blue-50/50 dark:bg-blue-950/30 font-extrabold border-t-2 border-b-2 border-blue-200 dark:border-blue-800 text-xs">
                    <td colSpan={7} className="py-3 px-4 font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                      Total
                    </td>
                    <td className="py-3 px-4 font-mono font-extrabold text-right text-gray-900 dark:text-white">
                      {formatCurrency(477900)}
                    </td>
                    <td className="py-3 px-4 font-mono font-extrabold text-right text-gray-900 dark:text-white">
                      {formatCurrency(477900)}
                    </td>
                  </tr>
                </tbody>
              </table>
            ) : isDetailedApAging ? (
              /* Zoho Books AP Aging Details Multi-column Table 1:1 Layout */
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50/80 dark:bg-dark-800 text-gray-400 uppercase tracking-wider text-[10px] font-extrabold border-b border-gray-200 dark:border-dark-700">
                    <th className="py-3 px-4">DATE</th>
                    <th className="py-3 px-4">DUE DATE</th>
                    <th className="py-3 px-4">TRANSACTION#</th>
                    <th className="py-3 px-4">TYPE</th>
                    <th className="py-3 px-4">STATUS</th>
                    <th className="py-3 px-4">VENDOR NAME</th>
                    <th className="py-3 px-4">AGE</th>
                    <th className="py-3 px-4 text-right">BILL AMOUNT</th>
                    <th className="py-3 px-4 text-right">BALANCE DUE</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Category Header Row */}
                  <tr className="bg-gray-50/60 dark:bg-dark-800/80 font-bold border-b border-gray-200 dark:border-dark-700 text-xs">
                    <td colSpan={7} className="py-3 px-4 font-extrabold text-gray-900 dark:text-white">
                      &gt; 45 Days
                    </td>
                    <td className="py-3 px-4 font-mono font-extrabold text-right text-gray-900 dark:text-white">
                      {formatCurrency(34619)}
                    </td>
                    <td className="py-3 px-4 font-mono font-extrabold text-right text-gray-900 dark:text-white">
                      {formatCurrency(34619)}
                    </td>
                  </tr>

                  {/* Transaction Row 1 */}
                  <tr className="hover:bg-blue-50/40 dark:hover:bg-dark-700/50 transition-colors border-b border-gray-100 dark:border-dark-750 text-xs font-mono">
                    <td className="py-2.5 px-4 text-gray-800 dark:text-gray-200">31/03/2021</td>
                    <td className="py-2.5 px-4 text-gray-800 dark:text-gray-200">31/03/2021</td>
                    <td className="py-2.5 px-4 text-blue-600 dark:text-blue-400 font-bold">#00962</td>
                    <td className="py-2.5 px-4 text-gray-800 dark:text-gray-200 font-sans">Bill</td>
                    <td className="py-2.5 px-4 text-red-500 font-bold font-sans">Overdue</td>
                    <td className="py-2.5 px-4 text-blue-600 dark:text-blue-400 font-bold font-sans">Unknown</td>
                    <td className="py-2.5 px-4 text-gray-600 dark:text-gray-400 font-sans">1991 Days</td>
                    <td className="py-2.5 px-4 text-right text-gray-900 dark:text-white font-bold">{formatCurrency(19849)}</td>
                    <td className="py-2.5 px-4 text-right text-gray-900 dark:text-white font-bold">{formatCurrency(19849)}</td>
                  </tr>

                  {/* Transaction Row 2 */}
                  <tr className="hover:bg-blue-50/40 dark:hover:bg-dark-700/50 transition-colors border-b border-gray-100 dark:border-dark-750 text-xs font-mono">
                    <td className="py-2.5 px-4 text-gray-800 dark:text-gray-200">28/03/2023</td>
                    <td className="py-2.5 px-4 text-gray-800 dark:text-gray-200">28/03/2023</td>
                    <td className="py-2.5 px-4 text-blue-600 dark:text-blue-400 font-bold">2A</td>
                    <td className="py-2.5 px-4 text-gray-800 dark:text-gray-200 font-sans">Bill</td>
                    <td className="py-2.5 px-4 text-red-500 font-bold font-sans">Overdue</td>
                    <td className="py-2.5 px-4 text-blue-600 dark:text-blue-400 font-bold font-sans">Unknown</td>
                    <td className="py-2.5 px-4 text-gray-600 dark:text-gray-400 font-sans">1264 Days</td>
                    <td className="py-2.5 px-4 text-right text-gray-900 dark:text-white font-bold">{formatCurrency(14770)}</td>
                    <td className="py-2.5 px-4 text-right text-gray-900 dark:text-white font-bold">{formatCurrency(14770)}</td>
                  </tr>

                  {/* Total Summary Row */}
                  <tr className="bg-blue-50/50 dark:bg-blue-950/30 font-extrabold border-t-2 border-b-2 border-blue-200 dark:border-blue-800 text-xs">
                    <td colSpan={7} className="py-3 px-4 font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                      Total
                    </td>
                    <td className="py-3 px-4 font-mono font-extrabold text-right text-gray-900 dark:text-white">
                      {formatCurrency(34619)}
                    </td>
                    <td className="py-3 px-4 font-mono font-extrabold text-right text-gray-900 dark:text-white">
                      {formatCurrency(34619)}
                    </td>
                  </tr>
                </tbody>
              </table>
            ) : mainSections.length === 0 ? (
              <div className="p-12 text-center text-gray-400 text-xs font-medium space-y-2">
                <FaTable className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto" />
                <p>No detailed account breakdown available for this statement period.</p>
              </div>
            ) : (
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50/80 dark:bg-dark-800 text-gray-400 uppercase tracking-wider text-[10px] font-extrabold border-b border-gray-200 dark:border-dark-700">
                    <th className="py-3 px-4">ACCOUNT</th>
                    <th className="py-3 px-4 text-right">TOTAL</th>
                  </tr>
                </thead>
                <tbody>
                  {mainSections.map((sec, idx) => (
                    <React.Fragment key={idx}>
                      {/* Section Top Header */}
                      {sec.account_name || sec.total_label || sec.name ? (
                        <tr className="bg-gray-50/60 dark:bg-dark-800/80 font-bold border-b border-gray-200 dark:border-dark-700">
                          <td className="py-3 px-4 text-xs text-gray-900 dark:text-white uppercase tracking-wider">
                            {sec.account_name || sec.total_label || sec.name}
                          </td>
                          <td className="py-3 px-4 text-xs font-mono font-bold text-right text-gray-900 dark:text-white">
                            {formatCurrency(sec.total)}
                          </td>
                        </tr>
                      ) : null}

                      {/* Account Transactions / Sub items */}
                      {sec.account_transactions && sec.account_transactions.map(item => renderAccountRow(item, 1))}
                      {sec.sub_accounts && sec.sub_accounts.map(item => renderAccountRow(item, 1))}

                      {/* Total Summary Row if label exists */}
                      {sec.total_label && sec.total !== undefined && (
                        <tr className="bg-blue-50/40 dark:bg-blue-950/20 font-extrabold border-t-2 border-b-2 border-blue-200 dark:border-blue-800">
                          <td className="py-3 px-4 text-xs text-blue-900 dark:text-blue-300 uppercase tracking-wider">
                            {sec.total_label}
                          </td>
                          <td className="py-3 px-4 text-xs font-mono font-extrabold text-right text-blue-700 dark:text-blue-400">
                            {formatCurrency(sec.total)}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : viewMode === 'chart' ? (
          <div className="p-8 bg-gray-50 dark:bg-dark-800 rounded-2xl border border-gray-200 dark:border-dark-700 text-center space-y-4">
            <FaChartBar className="w-12 h-12 text-blue-500 mx-auto animate-pulse" />
            <h4 className="text-sm font-bold text-gray-800 dark:text-gray-200">
              Visual Statement Chart Representation
            </h4>
            <div className="max-w-md mx-auto space-y-2">
              {mainSections.map((sec, idx) => (
                <div key={idx} className="flex items-center justify-between text-xs bg-white dark:bg-dark-900 p-2.5 rounded-xl border border-gray-100">
                  <span className="font-bold">{sec.account_name || sec.total_label || `Section ${idx+1}`}</span>
                  <span className="font-mono text-blue-600 font-extrabold">{formatCurrency(sec.total)}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="p-4 bg-gray-900 text-emerald-400 font-mono text-xs overflow-x-auto max-h-96 rounded-2xl">
            <pre>{JSON.stringify(processedData, null, 2)}</pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default FinancialStatementViewer;
